import { CardContent, CardFooter } from '@/components/ui/card';
import { VoucherPagination } from '@/components/vouchers/VoucherPagination';
import React from 'react';

type VoucherGridBaseProps<TItem> = {
  items: TItem[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  emptyMessage: string;
  currentPage: number;
  totalPages: number;
  totalVouchers: number;
  onPageChange: (page: number) => void;
  renderItem: (item: TItem, index: number) => React.ReactNode;
  gridClassName?: string;
};

export function VoucherGridBase<TItem>(props: VoucherGridBaseProps<TItem>) {
  const {
    items,
    isLoading,
    isError,
    error,
    emptyMessage,
    currentPage,
    totalPages,
    totalVouchers,
    onPageChange,
    renderItem,
    gridClassName,
  } = props;

  if (isLoading) {
    return (
      <CardContent className='pt-6'>
        <div className='flex items-center justify-center py-12'>
          <div className='text-center space-y-2'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto' />
            <p className='text-muted-foreground'>Gutscheine werden geladen...</p>
          </div>
        </div>
      </CardContent>
    );
  }

  if (isError) {
    return (
      <CardContent className='pt-6'>
        <div className='text-center py-12'>
          <p className='text-destructive text-lg font-medium mb-2'>Fehler beim Laden der Gutscheine</p>
          <p className='text-muted-foreground'>{error instanceof Error ? error.message : 'Unbekannter Fehler'}</p>
        </div>
      </CardContent>
    );
  }

  return (
    <>
      <CardContent className='p-4 sm:p-10'>
        {items.length === 0 ? (
          <div className='text-center py-12'>
            <h3 className='text-lg font-semibold mb-2'>Keine Gutscheine verfügbar</h3>
            <p className='text-muted-foreground'>{emptyMessage}</p>
          </div>
        ) : (
          <div className={gridClassName ?? 'grid gap-8 md:grid-cols-2 lg:grid-cols-3'}>
            {items.map((item, index) => renderItem(item, index))}
          </div>
        )}
      </CardContent>

      {items.length > 0 && (
        <CardFooter className='px-10 pb-10 pt-0'>
          <VoucherPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalVouchers={totalVouchers}
            onPageChange={onPageChange}
          />
        </CardFooter>
      )}
    </>
  );
}
